/**
 * Parser for the Czech GPC / ABO bank statement format.
 *
 * GPC files are fixed-width: every line is exactly 128 characters, CRLF-terminated.
 * Amounts are unsigned integers in hellers (1/100 of the unit); dates are DDMMYY.
 * A single file may contain several statements — one `074` header per account,
 * each followed by its `075` items.
 *
 * Field offsets below are 1-based inclusive, matching the ABO specification, and were
 * verified against real Komerční banka statements using two invariants that must hold
 * for every statement:
 *   opening - debitTurnover + creditTurnover === closing
 *   sum(debit items) === debitTurnover  and  sum(credit items) === creditTurnover
 *
 * Two details differ from most published descriptions of the format and matter here:
 *  - Positions 72-81 are a *6-character* counterparty bank code followed by a
 *    4-character constant symbol, not the commonly documented 4+6 split.
 *  - Positions 92-97 (valuta) are left as zeroes by KB, so the posting date at
 *    123-128 is the only usable date.
 */

/** Accounting code at position 61 of a `075` record. */
export type GpcAccountingCode = "1" | "2" | "4" | "5";

export interface GpcItem {
  /** Posting date, ISO `YYYY-MM-DD`. */
  postingDate: string;
  /** Signed amount in major units — negative for debits. */
  amount: number;
  accountingCode: GpcAccountingCode;
  /** Counterparty account, e.g. `115-7200457550`. Null for card transactions. */
  counterAccount: string | null;
  /** Counterparty bank code, e.g. `0100`. */
  counterBankCode: string | null;
  counterName: string;
  variableSymbol: string | null;
  constantSymbol: string | null;
  specificSymbol: string | null;
  /** Bank document id — `MMDD` plus a counter. Unique only within a month/day. */
  documentId: string;
  /** Bank-internal type code (positions 118-122). Kept for audit, not interpreted. */
  typeCode: string;
  /** The untouched 128-character source line. */
  raw: string;
}

export interface GpcStatement {
  /** Statement holder's account number, leading zeroes stripped. */
  account: string;
  accountPrefix: string | null;
  holderName: string;
  /** Date the opening balance refers to, ISO `YYYY-MM-DD`. */
  openingBalanceDate: string;
  /** Date the statement file was produced, ISO `YYYY-MM-DD`. */
  fileDate: string;
  /** Sequence number the bank assigns to the statement (positions 106-108). */
  sequenceNumber: number;
  openingBalance: number;
  closingBalance: number;
  debitTurnover: number;
  creditTurnover: number;
  items: GpcItem[];
  /** Non-fatal problems. A statement with warnings is still importable. */
  warnings: string[];
}

const LINE_LENGTH = 128;

/** Record types we knowingly ignore — informational blocks in some bank dialects. */
const IGNORED_RECORD_TYPES = new Set(["076", "078", "079"]);

/**
 * Decode raw bytes as Windows-1250. GPC is a pre-Unicode format and banks emit
 * CP1250 or ISO-8859-2; treating it as UTF-8 mangles Czech diacritics.
 */
/**
 * Larger than any real monthly or yearly statement (a GPC record is 128
 * bytes, so 20 MB is ~160 000 movements). Callers check `File.size` against
 * it before reading; the parser checks again, so no caller can forget.
 */
export const MAX_GPC_BYTES = 20 * 1024 * 1024;
/**
 * Lines at most. A 20 MB file of one-character lines would otherwise become
 * 10 million strings and freeze or crash the tab before any record is read.
 */
export const MAX_GPC_LINES = 250_000;

function countLines(text: string, stopAfter: number): number {
  let count = 1;
  for (let i = text.indexOf("\n"); i !== -1; i = text.indexOf("\n", i + 1)) {
    if (++count > stopAfter) break;
  }
  return count;
}

function decode(bytes: ArrayBuffer): string {
  for (const encoding of ["windows-1250", "iso-8859-2", "latin1"]) {
    try {
      return new TextDecoder(encoding).decode(bytes);
    } catch {
      // Encoding unavailable in this runtime — fall through to the next candidate.
    }
  }
  throw new Error("No single-byte decoder available to read the GPC file");
}

function field(line: string, from: number, to: number): string {
  return line.slice(from - 1, to);
}

function digits(line: string, from: number, to: number, label: string, lineNo: number): number {
  const raw = field(line, from, to);
  if (!/^\d+$/.test(raw)) {
    throw new Error(`Line ${lineNo}: ${label} is not numeric ("${raw}")`);
  }
  return parseInt(raw, 10);
}

/** Hellers to major units, avoiding float drift on the division. */
function hellersToAmount(hellers: number): number {
  return Math.round(hellers) / 100;
}

/**
 * `DDMMYY` to ISO `YYYY-MM-DD`. Two-digit years are 2000-based; the format
 * predates that assumption but no Czech bank emits pre-2000 statements today.
 */
function parseGpcDate(raw: string, label: string, lineNo: number): string {
  if (!/^\d{6}$/.test(raw)) {
    throw new Error(`Line ${lineNo}: ${label} is not a DDMMYY date ("${raw}")`);
  }
  const day = parseInt(raw.slice(0, 2), 10);
  const month = parseInt(raw.slice(2, 4), 10);
  const year = 2000 + parseInt(raw.slice(4, 6), 10);

  // Round-trip through Date to reject impossible dates such as 31.02.
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error(`Line ${lineNo}: ${label} is not a valid date ("${raw}")`);
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Positions 60/75 hold `-` for negative; KB writes `0` rather than `+` for positives. */
function applySign(value: number, sign: string): number {
  return sign === "-" ? -value : value;
}

/** Strip leading zeroes from a symbol field; all-zero means "not supplied". */
function optionalNumber(raw: string): string | null {
  const trimmed = raw.replace(/^0+/, "");
  return trimmed === "" ? null : trimmed;
}

/**
 * Positions 20-35 of a `075` record: a 10-digit account number followed by a
 * 6-digit prefix. Rendered the Czech way, `prefix-account`.
 */
function parseCounterAccount(line: string): string | null {
  const account = optionalNumber(field(line, 20, 29));
  const prefix = optionalNumber(field(line, 30, 35));
  if (!account) return null;
  return prefix ? `${prefix}-${account}` : account;
}

function parseItem(line: string, lineNo: number, warnings: string[]): GpcItem {
  const code = field(line, 61, 61);
  if (code !== "1" && code !== "2" && code !== "4" && code !== "5") {
    throw new Error(`Line ${lineNo}: unknown accounting code "${code}"`);
  }

  // Items carry an unsigned amount; the accounting code decides the direction.
  // 1 = debit, 2 = credit, 4 = reversal of a debit, 5 = reversal of a credit.
  const magnitude = hellersToAmount(digits(line, 49, 60, "amount", lineNo));
  const isDebit = code === "1" || code === "4";

  // 72-77 is the bank code zero-padded to six; anything else means our offsets
  // are wrong for this bank's dialect, so surface it rather than guessing.
  const bankField = field(line, 72, 77);
  if (!/^00\d{4}$/.test(bankField)) {
    warnings.push(`Line ${lineNo}: unexpected bank code field "${bankField}"`);
  }
  const counterBankCode = /^\d{6}$/.test(bankField) ? bankField.slice(2) : null;

  return {
    postingDate: parseGpcDate(field(line, 123, 128), "posting date", lineNo),
    amount: isDebit ? -magnitude : magnitude,
    accountingCode: code,
    counterAccount: parseCounterAccount(line),
    counterBankCode: counterBankCode === "0000" ? null : counterBankCode,
    counterName: field(line, 98, 117).trim(),
    variableSymbol: optionalNumber(field(line, 62, 71)),
    constantSymbol: optionalNumber(field(line, 78, 81)),
    specificSymbol: optionalNumber(field(line, 82, 91)),
    documentId: field(line, 36, 48),
    typeCode: field(line, 118, 122),
    raw: line,
  };
}

function parseHeader(line: string, lineNo: number): GpcStatement {
  return {
    account: optionalNumber(field(line, 4, 13)) ?? "",
    accountPrefix: optionalNumber(field(line, 14, 19)),
    holderName: field(line, 20, 39).trim(),
    openingBalanceDate: parseGpcDate(field(line, 40, 45), "opening balance date", lineNo),
    fileDate: parseGpcDate(field(line, 109, 114), "file date", lineNo),
    sequenceNumber: digits(line, 106, 108, "sequence number", lineNo),
    openingBalance: applySign(
      hellersToAmount(digits(line, 46, 59, "opening balance", lineNo)),
      field(line, 60, 60)
    ),
    closingBalance: applySign(
      hellersToAmount(digits(line, 61, 74, "closing balance", lineNo)),
      field(line, 75, 75)
    ),
    debitTurnover: hellersToAmount(digits(line, 76, 89, "debit turnover", lineNo)),
    creditTurnover: hellersToAmount(digits(line, 91, 104, "credit turnover", lineNo)),
    items: [],
    warnings: [],
  };
}

/**
 * Check the statement against itself. Both invariants hold on well-formed files, so a
 * failure means either a truncated download or a dialect we have mis-parsed. It is
 * reported rather than thrown: the user still needs to be able to import and inspect.
 */
function verifyTotals(statement: GpcStatement): void {
  const { openingBalance, closingBalance, debitTurnover, creditTurnover, items } = statement;

  // Compare in hellers so repeated float addition cannot produce a spurious mismatch.
  const cents = (n: number) => Math.round(n * 100);
  const expectedClosing = cents(openingBalance) - cents(debitTurnover) + cents(creditTurnover);
  if (expectedClosing !== cents(closingBalance)) {
    statement.warnings.push(
      `Balances do not add up: ${openingBalance} - ${debitTurnover} + ${creditTurnover} ≠ ${closingBalance}`
    );
  }

  let debits = 0;
  let credits = 0;
  for (const item of items) {
    if (item.amount < 0) debits += cents(-item.amount);
    else credits += cents(item.amount);
  }
  if (debits !== cents(debitTurnover)) {
    statement.warnings.push(
      `Debit items total ${debits / 100} but the header declares ${debitTurnover}`
    );
  }
  if (credits !== cents(creditTurnover)) {
    statement.warnings.push(
      `Credit items total ${credits / 100} but the header declares ${creditTurnover}`
    );
  }
}

/**
 * Parse a GPC/ABO file into one statement per `074` header record.
 *
 * @throws if the file is not GPC at all, or if a record is structurally broken.
 *   Recoverable oddities land in each statement's `warnings` instead.
 */
export function parseGpc(bytes: ArrayBuffer): GpcStatement[] {
  // Keep trailing spaces: they are significant filler, and stripping them here would
  // make every well-formed record look truncated.
  if (bytes.byteLength > MAX_GPC_BYTES) {
    throw new Error(
      `The file is ${Math.ceil(bytes.byteLength / 1024 / 1024)} MB; a GPC statement is at most ${MAX_GPC_BYTES / 1024 / 1024} MB.`
    );
  }
  const text = decode(bytes);
  if (countLines(text, MAX_GPC_LINES) > MAX_GPC_LINES) {
    throw new Error(
      `The file has more than ${MAX_GPC_LINES} lines. This does not look like a GPC statement.`
    );
  }
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.replace(/\r$/, ""))
    .filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    throw new Error("The file is empty");
  }

  const statements: GpcStatement[] = [];
  const shortLines: number[] = [];
  const ignoredTypes = new Set<string>();
  let current: GpcStatement | null = null;

  lines.forEach((rawLine, index) => {
    const lineNo = index + 1;
    if (rawLine.length > LINE_LENGTH) {
      throw new Error(
        `Line ${lineNo} is ${rawLine.length} characters; GPC records are ${LINE_LENGTH}. This does not look like a GPC file.`
      );
    }
    // Trailing filler is often stripped in transit; restore it so offsets stay valid.
    if (rawLine.length < LINE_LENGTH) shortLines.push(lineNo);
    const line = rawLine.padEnd(LINE_LENGTH, " ");

    const recordType = field(line, 1, 3);
    if (recordType === "074") {
      current = parseHeader(line, lineNo);
      statements.push(current);
      return;
    }
    if (recordType === "075") {
      if (!current) {
        throw new Error(`Line ${lineNo}: transaction record appears before any statement header`);
      }
      current.items.push(parseItem(line, lineNo, current.warnings));
      return;
    }
    if (IGNORED_RECORD_TYPES.has(recordType)) {
      ignoredTypes.add(recordType);
      return;
    }
    throw new Error(`Line ${lineNo}: unknown record type "${recordType}"`);
  });

  if (statements.length === 0) {
    throw new Error("No statement header (record 074) found — this is not a GPC statement file");
  }

  for (const statement of statements) {
    if (shortLines.length > 0) {
      statement.warnings.push(
        `${shortLines.length} line(s) were shorter than ${LINE_LENGTH} characters and were padded`
      );
    }
    for (const type of ignoredTypes) {
      statement.warnings.push(`Ignored unsupported record type ${type}`);
    }
    verifyTotals(statement);
  }

  return statements;
}
